import { Controller, Control, RegisterOptions } from 'react-hook-form';
import styles from './PhoneForm.module.scss';
import classNames from 'classnames';

export const formatPhoneNumber = (value: string) => {
  const digits = (value || '').replace(/\D/g, '');

  if (digits.length === 0) return '';

  let normalizedDigits: string;
  if (digits.startsWith('8')) {
    normalizedDigits = '7' + digits.slice(1);
  } else if (digits.startsWith('7')) {
    normalizedDigits = digits;
  } else {
    normalizedDigits = '7' + digits;
  }

  if (normalizedDigits.length >= 2 && normalizedDigits[1] !== '9') {
    normalizedDigits = '79' + normalizedDigits.slice(2);
  }

  normalizedDigits = normalizedDigits.slice(0, 11);

  if (normalizedDigits.length <= 1) return `+${normalizedDigits}`;
  if (normalizedDigits.length <= 4)
    return `+${normalizedDigits.slice(0, 1)} (${normalizedDigits.slice(1)}`;
  if (normalizedDigits.length <= 7)
    return `+${normalizedDigits.slice(0, 1)} (${normalizedDigits.slice(
      1,
      4,
    )}) ${normalizedDigits.slice(4)}`;
  if (normalizedDigits.length <= 9)
    return `+${normalizedDigits.slice(0, 1)} (${normalizedDigits.slice(
      1,
      4,
    )}) ${normalizedDigits.slice(4, 7)}-${normalizedDigits.slice(7)}`;
  return `+${normalizedDigits.slice(0, 1)} (${normalizedDigits.slice(
    1,
    4,
  )}) ${normalizedDigits.slice(4, 7)}-${normalizedDigits.slice(
    7,
    9,
  )}-${normalizedDigits.slice(9, 11)}`;
};

const handlePhoneChangeLogic = (
  event: React.ChangeEvent<HTMLInputElement>,
  onChange: (value: string) => void,
  controlled?: boolean,
) => {
  const input = event.target;
  const cursorPosition = input.selectionStart;
  const value = event.target.value;

  const digits = value.replace(/\D/g, '');

  if (digits.length === 0) {
    onChange('');
    if (!controlled) {
      setTimeout(() => {
        input.value = '';
        input.setSelectionRange(0, 0);
      }, 0);
    }
    return;
  }

  let normalizedDigits: string;

  if (digits.startsWith('8')) {
    normalizedDigits = '7' + digits.slice(1);
  } else if (digits.startsWith('7')) {
    normalizedDigits = digits;
  } else {
    normalizedDigits = '7' + digits;
  }

  if (normalizedDigits.length >= 2 && normalizedDigits[1] !== '9') {
    normalizedDigits = '79' + normalizedDigits.slice(2);
  } else if (normalizedDigits.length === 1) {
    normalizedDigits = '7';
  }

  normalizedDigits = normalizedDigits.slice(0, 11);

  const cleanValue =
    normalizedDigits.length === 1 ? normalizedDigits : normalizedDigits.slice(1);
  onChange(cleanValue);

  const formattedValue = formatPhoneNumber(normalizedDigits);

  const pos = cursorPosition ?? 0;
  const originalDigitsBefore = value.slice(0, pos).replace(/\D/g, '').length;

  let adjustedDigitsBefore = originalDigitsBefore;
  if (!digits.startsWith('7') && !digits.startsWith('8') && digits.length > 0) {
    adjustedDigitsBefore += 1;
  }

  let newCursorPos = 0;
  let digitCount = 0;

  for (let i = 0; i < formattedValue.length; i++) {
    if (/\d/.test(formattedValue[i])) {
      digitCount++;
      if (digitCount === adjustedDigitsBefore) {
        newCursorPos = i + 1;
        break;
      }
    }
    if (digitCount < adjustedDigitsBefore) {
      newCursorPos = i + 1;
    }
  }

  setTimeout(() => {
    if (!controlled && input.value !== formattedValue) {
      input.value = formattedValue;
    }
    input.setSelectionRange(newCursorPos, newCursorPos);
  }, 0);
};

const handlePhonePasteLogic = (
  event: React.ClipboardEvent<HTMLInputElement>,
  onChange: (value: string) => void,
  controlled?: boolean,
) => {
  event.preventDefault();
  const pastedText = event.clipboardData.getData('text');
  const digits = pastedText.replace(/\D/g, '');

  if (digits.length === 0) {
    onChange('');
    if (!controlled) {
      setTimeout(() => {
        (event.target as HTMLInputElement).value = '';
      }, 0);
    }
    return;
  }

  let normalizedDigits: string;
  if (digits.startsWith('8')) {
    normalizedDigits = '7' + digits.slice(1);
  } else if (digits.startsWith('7')) {
    normalizedDigits = digits;
  } else {
    normalizedDigits = '7' + digits;
  }

  if (normalizedDigits.length >= 2 && normalizedDigits[1] !== '9') {
    normalizedDigits = '79' + normalizedDigits.slice(2);
  }

  normalizedDigits = normalizedDigits.slice(0, 11);

  const cleanValue =
    normalizedDigits.length === 1 ? normalizedDigits : normalizedDigits.slice(1);
  onChange(cleanValue);

  if (!controlled) {
    const formattedValue = formatPhoneNumber(normalizedDigits);
    setTimeout(() => {
      const target = event.target as HTMLInputElement;
      target.value = formattedValue;
      target.setSelectionRange(formattedValue.length, formattedValue.length);
    }, 0);
  }
};

type PhoneFormPropsBase = {
  error?: string;
  disabled?: boolean;
  readOnly?: boolean;
  inputClassName?: string;
  inputStyle?: React.CSSProperties;
  placeholder?: string;
  id?: string;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  onBlur?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onAfterChange?: () => void;
};

type PhoneFormPropsRHF = PhoneFormPropsBase & {
  control: Control<Record<string, unknown>>;
  name: string;
  rules?: RegisterOptions<Record<string, unknown>, string>;
  value?: never;
  onChange?: never;
};

type PhoneFormPropsStandalone = PhoneFormPropsBase & {
  value: string;
  onChange: (value: string) => void;
  control?: never;
  name?: never;
};

export type PhoneFormProps = PhoneFormPropsRHF | PhoneFormPropsStandalone;

function isRHFProps(props: PhoneFormProps): props is PhoneFormPropsRHF {
  return 'control' in props && props.control != null;
}

export default function PhoneForm(props: PhoneFormProps) {
  const {
    error,
    disabled,
    readOnly,
    inputClassName,
    inputStyle,
    placeholder = '+7 (000) 000-00-00',
    id = 'phone',
    inputRef,
    onBlur,
    onKeyDown,
    onAfterChange,
  } = props;

  const wrapOnChange = (fn: (value: string) => void) => (value: string) => {
    fn(value);
    onAfterChange?.();
  };

  const getDisplayValue = (val: string) => {
    if (!val) return '';
    return val === '7' ? formatPhoneNumber('7') : formatPhoneNumber('7' + val);
  };

  if (!isRHFProps(props)) {
    const { value, onChange } = props;
    const displayValue = getDisplayValue(value);
    return (
      <input
        ref={(el) => {
          if (inputRef)
            (inputRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
        }}
        type='text'
        className={classNames(
          styles.input,
          inputClassName,
          error ? styles.input_error : '',
        )}
        placeholder={placeholder}
        onBlur={onBlur}
        required
        autoComplete='username'
        name='username'
        id={id}
        readOnly={readOnly}
        disabled={disabled}
        value={displayValue}
        style={inputStyle}
        onPaste={(e) => handlePhonePasteLogic(e, wrapOnChange(onChange), true)}
        onChange={(e) => handlePhoneChangeLogic(e, wrapOnChange(onChange), true)}
        onKeyDown={onKeyDown}
      />
    );
  }

  const { control, name, rules } = props;
  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field: { value, onChange, onBlur: fieldOnBlur } }) => {
        const strValue = typeof value === 'string' ? value : '';
        const formatted = getDisplayValue(strValue);
        return (
          <input
            ref={(el) => {
              if (inputRef)
                (inputRef as React.MutableRefObject<HTMLInputElement | null>).current =
                  el;
            }}
            type='text'
            className={classNames(
              styles.input,
              inputClassName,
              error ? styles.input_error : '',
            )}
            placeholder={placeholder}
            onBlur={fieldOnBlur}
            required
            autoComplete='username'
            name='username'
            id={id}
            readOnly={readOnly}
            disabled={disabled}
            value={formatted}
            style={inputStyle}
            onPaste={(e) => handlePhonePasteLogic(e, wrapOnChange(onChange), true)}
            onChange={(e) => handlePhoneChangeLogic(e, wrapOnChange(onChange), true)}
            onKeyDown={onKeyDown}
          />
        );
      }}
    />
  );
}
